import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Card,
  Descriptions,
  Table,
  Timeline,
  Tag,
  Button,
  Spin,
  message,
  Space
} from 'antd'
import dayjs from 'dayjs'
import axios from 'axios'

function minuteToTime(minute) {
  if (minute == null) return '-'

  const hour = Math.floor(Number(minute) / 60)
  const min = Number(minute) % 60

  return `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`
}

function formatDate(value) {
  if (!value) return '-'
  return dayjs(value).format('YYYY-MM-DD')
}

function formatDateTime(value) {
  if (!value) return '-'
  return dayjs(value).format('YYYY-MM-DD HH:mm')
}

function getStatusTag(status) {
  if (status === 'BOOKED') {
    return <Tag color="blue">예약</Tag>
  }

  if (status === 'CANCELED') {
    return <Tag color="red">취소</Tag>
  }

  return <Tag>{status || '-'}</Tag>
}

export default function AdminReservationDetail(props) {
  const { reservationId, onClose } = props
  // const { reservationId } = useParams()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [reservation, setReservation] = useState(null)
  const [slots, setSlots] = useState([])

  const fetchDetail = async () => {
    setLoading(true)

    try {
      const res = await axios.get(`/api/admin/reservations/${reservationId}`)

      setReservation(res.data.data.reservation)
      setSlots(res.data.data.slots || [])
    } catch (error) {
      console.error(error)
      message.error('예약 상세 정보를 불러오지 못했습니다.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDetail()
  }, [reservationId])

  const timelineItems = useMemo(() => {
    if (!reservation) return []

    const items = [
      {
        color: 'blue',
        children: (
          <div>
            <strong>예약 생성</strong>
            <div>{formatDateTime(reservation.reservedTime || reservation.creationTime)}</div>
          </div>
        )
      }
    ]

    slots.forEach(slot => {
      items.push({
        color: slot.mappingStatus === 'CANCELED' ? 'red' : 'green',
        children: (
          <div>
            <strong>
              {slot.shopName} / {formatDate(slot.slotDate)} {minuteToTime(slot.startMinuteOfDay)}
            </strong>
            <div>
              {slot.mappingStatus === 'CANCELED'
                ? `슬롯 취소 ${formatDateTime(slot.mappingCanceledTime)}`
                : '슬롯 예약'}
            </div>
          </div>
        )
      })
    })

    if (reservation.reservationStatus === 'CANCELED') {
      items.push({
        color: 'red',
        children: (
          <div>
            <strong>예약 취소</strong>
            <div>{formatDateTime(reservation.canceledTime)}</div>
            {reservation.cancelReason && <div>{reservation.cancelReason}</div>}
          </div>
        )
      })
    }

    return items
  }, [reservation, slots])

  const slotColumns = [
    {
      title: '업체',
      dataIndex: 'shopName',
      width: 160
    },
    {
      title: '날짜',
      dataIndex: 'slotDate',
      width: 120,
      render: formatDate
    },
    {
      title: '시간',
      width: 140,
      render: (_, row) => (
        <span>
          {minuteToTime(row.startMinuteOfDay)} ~ {minuteToTime(row.endMinuteOfDay)}
        </span>
      )
    },
    {
      title: '룸 ID',
      dataIndex: 'roomId',
      width: 90
    },
    {
      title: '슬롯 상태',
      dataIndex: 'slotStatus',
      width: 110,
      render: getStatusTag
    },
    {
      title: '매핑 상태',
      dataIndex: 'mappingStatus',
      width: 110,
      render: getStatusTag
    },
    {
      title: '예약 처리 시각',
      dataIndex: 'bookingTime',
      width: 160,
      render: formatDateTime
    }
  ]

  if (loading && !reservation) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin />
      </div>
    )
  }

  if (!reservation) {
    return (
      <div style={{ padding: 24 }}>
        <Button onClick={() => navigate(-1)}>뒤로가기</Button>
      </div>
    )
  }

  return (
    <div style={{ padding: 24 }}>
      {/* <Space style={{ marginBottom: 16 }}>
        <Button onClick={() => navigate(-1)}>뒤로가기</Button>
        <Button onClick={fetchDetail}>새로고침</Button>
      </Space> */}

      <Card
        title={`예약 상세 - ${reservation.reservationCode}`}
        style={{ marginBottom: 16 }}
      >
        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label="예약 상태">
            {getStatusTag(reservation.reservationStatus)}
          </Descriptions.Item>

          <Descriptions.Item label="예약 코드">
            {reservation.reservationCode}
          </Descriptions.Item>

          <Descriptions.Item label="고객명">
            {reservation.customerName}
          </Descriptions.Item>

          <Descriptions.Item label="외부 고객 ID">
            {reservation.externalCustomerId || '-'}
          </Descriptions.Item>

          <Descriptions.Item label="플래너명">
            {reservation.plannerName || '-'}
          </Descriptions.Item>

          <Descriptions.Item label="플래너 연락처">
            {reservation.plannerPhoneNumber || '-'}
          </Descriptions.Item>

          <Descriptions.Item label="예식 예정일">
            {reservation.weddingDateUndecided
              ? '미정'
              : formatDate(reservation.weddingDate)}
          </Descriptions.Item>

          <Descriptions.Item label="촬영 예정일">
            {reservation.shootingDateUndecided
              ? '미정'
              : formatDate(reservation.shootingDate)}
          </Descriptions.Item>

          <Descriptions.Item label="예약 시각">
            {formatDateTime(reservation.reservedTime)}
          </Descriptions.Item>

          <Descriptions.Item label="취소 시각">
            {formatDateTime(reservation.canceledTime)}
          </Descriptions.Item>

          <Descriptions.Item label="비고" span={2}>
            {reservation.notice || '-'}
          </Descriptions.Item>

          <Descriptions.Item label="관리 메모" span={2}>
            {reservation.memo || '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="예약 슬롯" style={{ marginBottom: 16 }}>
        <Table
          rowKey={row => row.reservationOpenSlotId || row.openSlotId}
          columns={slotColumns}
          dataSource={slots}
          pagination={false}
          size="small"
        />
      </Card>

      <Card title="타임라인">
        <Timeline items={timelineItems} />
      </Card>
    </div>
  )
}